import { useState } from 'react';
import { omit } from 'lodash';
import ReactGA from 'react-ga4';
import { UaEventOptions } from 'react-ga4/types/ga4';
import { AppointmentType } from 'types/UFOTypes';
import humanize from 'utils/humanize';
import { ENVIRONMENT } from '../../consts/environment';
import { AnalyticsAction, AnalyticsCategory } from './types';

const { REACT_APP_GOOGLE_ANALYTICS } = ENVIRONMENT;

interface UfoEventArgs extends UaEventOptions {
  category: AnalyticsCategory;
  action: AnalyticsAction;
  isDealer: boolean;
  orderType?: AppointmentType;
}

type UfoEventParams = [UfoEventArgs];
type UfoEventFunction = (...params: UfoEventParams) => void;

interface GoogleAnalyticsUtils extends Omit<typeof ReactGA, 'initialize' | 'event'> {
  event: UfoEventFunction;
}

const getFullAction = (args: UfoEventArgs) => {
  const orderType = args.orderType ? `${humanize(args.orderType)} Order-` : '';
  return `${orderType}${args.action}`;
};

const trackEvent = (args: UfoEventArgs) => {
  const { category, isDealer } = args;
  ReactGA.event({
    category,
    action: getFullAction(args),
    label: isDealer ? 'provider' : 'fleet',
  });
};

export const useGoogleAnalytics = (): GoogleAnalyticsUtils => {
  const [initialized, setInitialized] = useState(false);
  if (!initialized) {
    ReactGA.initialize(REACT_APP_GOOGLE_ANALYTICS);
    setInitialized(true);
  }

  return {
    ...omit(ReactGA, 'initialize', 'event'),
    event: trackEvent,
  };
};
